// frontend/src/store/messages.js
import api from '@/api'

export function makeMessagesModule () {
  const state = () => ({
    inbox: [],
    sent: [],
    unreadCount: 0,
    loading: false,
    error: null
  })

  const getters = {
    inbox: s => s.inbox,
    sent: s => s.sent,
    unreadCount: s => s.unreadCount,
    isLoading: s => s.loading,
    error: s => s.error
  }

  const mutations = {
    SET_LOADING (s, v) { s.loading = !!v },
    SET_ERROR (s, e) { s.error = e || null },
    SET_INBOX (s, rows) { s.inbox = Array.isArray(rows) ? rows : [] },
    SET_SENT (s, rows) { s.sent = Array.isArray(rows) ? rows : [] },
    SET_UNREAD (s, n) { s.unreadCount = Number(n || 0) },

    // Update message status in-place (snappy UI) + recompute unreadCount
    SET_MESSAGE_STATUS (s, { id, status }) {
      const sid = String(id)
      const next = String(status || '').toUpperCase()

      const row = (Array.isArray(s.inbox) ? s.inbox : []).find(x => String(x.id) === sid)
      if (row) row.status = next

      const statusOf = (x) => String(x && (x.status || x._status || x.status_text) || '').toUpperCase()
      s.unreadCount = (Array.isArray(s.inbox) ? s.inbox : []).filter(x => statusOf(x) === 'UNREAD').length
    }
  }

  function hasToken () {
    try { return !!localStorage.getItem('edda_token') } catch (e) { return false }
  }

  function statusOf (x) {
    return String(x && (x.status || x._status || x.status_text) || '').toUpperCase()
  }

  const actions = {
    // One loading state for the whole refresh (no flicker)
    async refresh ({ commit, dispatch }) {
      commit('SET_ERROR', null)

      if (!hasToken()) {
        commit('SET_INBOX', [])
        commit('SET_SENT', [])
        commit('SET_UNREAD', 0)
        return
      }

      commit('SET_LOADING', true)
      try {
        await Promise.all([dispatch('loadInbox'), dispatch('loadSent')])
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async loadInbox ({ commit }) {
      commit('SET_ERROR', null)

      if (!hasToken()) {
        commit('SET_INBOX', [])
        commit('SET_UNREAD', 0)
        return
      }

      try {
        // NOTE: baseURL already ends with /api
        const res = await api.get('/messages/inbox')
        const rows = (res && res.data) ? res.data : []
        commit('SET_INBOX', rows)
        commit('SET_UNREAD', rows.filter(x => statusOf(x) === 'UNREAD').length)
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
          (e && e.message) ||
          'Failed to load inbox'
        commit('SET_ERROR', msg)
        // keep existing inbox; don’t nuke UI on transient errors
      }
    },

    async loadSent ({ commit }) {
      commit('SET_ERROR', null)

      if (!hasToken()) {
        commit('SET_SENT', [])
        return
      }

      try {
        const res = await api.get('/messages/sent')
        commit('SET_SENT', (res && res.data) ? res.data : [])
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
          (e && e.message) ||
          'Failed to load sent'
        commit('SET_ERROR', msg)
      }
    },

    async markRead ({ commit }, id) {
      if (!hasToken()) return
      await api.patch(`/messages/${id}`, { status: 'READ' })
      commit('SET_MESSAGE_STATUS', { id, status: 'READ' })
    },

    async markDone ({ commit }, id) {
      if (!hasToken()) return
      await api.patch(`/messages/${id}`, { status: 'DONE' })
      commit('SET_MESSAGE_STATUS', { id, status: 'DONE' })
    },

    async sendMessage ({ commit, dispatch }, payload) {
      commit('SET_ERROR', null)
      if (!hasToken()) throw new Error('No JWT token (edda_token) found. Login again.')

      commit('SET_LOADING', true)
      try {
        await api.post('/messages', payload)
        // Refresh both sides to reflect any new unread + sent record
        await Promise.all([dispatch('loadSent'), dispatch('loadInbox')])
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
          (e && e.message) ||
          'Failed to send message'
        commit('SET_ERROR', msg)
        throw new Error(msg)
      } finally {
        commit('SET_LOADING', false)
      }
    }
  }

  return { namespaced: true, state, getters, mutations, actions }
}
